<script setup lang="ts">
/**
 * The one way a sheet of paper (or a dark panel) arrives over a scrim and
 * leaves again. Wrap the scrim element, which carries the `v-if`; its first
 * element child is the sheet.
 *
 * life-layer.md §3 P0: the scrim fades in 220 ms; the sheet comes up from
 * `scale .96, y 8, opacity 0` over 300 ms on the `--pop` overshoot; it leaves
 * in 180 ms on `power2.in` (`cubic-bezier(.55, .085, .68, .53)`).
 *
 * Driven with the Web Animations API rather than CSS classes because the
 * scrim and the sheet are two elements and `<Transition>` tracks one. It
 * animates the *individual* `scale` / `translate` properties, not `transform`,
 * so the slight `rotate(-0.6deg)` the paper dialogs rest at is not replaced
 * mid-animation (and the sheet does not snap straight at the end).
 *
 * Owns motion only. Focus, Escape and scrim clicks stay with the dialog.
 * Reduced motion: no animation, the sheet is simply there / gone.
 */
import { POP, reducedMotion } from '@/engine/motion';
import { playCue } from '@/audio/sfx';

/** life-layer.md §3 P0 — scrim fade, sheet arrival, sheet leave. */
const SCRIM_MS = 220;
const ENTER_MS = 300;
const LEAVE_MS = 180;
/** GSAP's `power2.in`, as a cubic-bezier, so the leave matches the rest of the motion language. */
const LEAVE_EASE = 'cubic-bezier(0.55, 0.085, 0.68, 0.53)';
const SHEET_RISE = '0 8px';
const SHEET_REST = '0 0';

function play(el: Element, entering: boolean, done: () => void): void {
  if (reducedMotion() || typeof el.animate !== 'function') {
    done();
    return;
  }
  // Hold the last frame while leaving so the sheet does not flash back before Vue removes it.
  const fill = entering ? 'backwards' : 'forwards';
  const animations: Animation[] = [
    el.animate(
      { opacity: entering ? [0, 1] : [1, 0] },
      { duration: entering ? SCRIM_MS : LEAVE_MS, easing: entering ? 'ease-out' : LEAVE_EASE, fill },
    ),
  ];
  const sheet = el.firstElementChild;
  if (sheet) {
    animations.push(
      sheet.animate(
        entering
          ? { opacity: [0, 1], scale: [0.96, 1], translate: [SHEET_RISE, SHEET_REST] }
          : { opacity: [1, 0], scale: [1, 0.96], translate: [SHEET_REST, SHEET_RISE] },
        { duration: entering ? ENTER_MS : LEAVE_MS, easing: entering ? POP : LEAVE_EASE, fill },
      ),
    );
  }
  // A cancelled animation rejects `finished`; either way the transition is over.
  void Promise.all(animations.map((a) => a.finished)).then(done, done);
}

/** Called when a sheet is toggled mid-flight: drop the half-played animations. */
function cancel(el: Element): void {
  if (typeof el.getAnimations !== 'function') return;
  el.getAnimations({ subtree: true }).forEach((a) => a.cancel());
}
</script>

<template>
  <Transition
    :css="false"
    @enter="(el, done) => { playCue('ui.open'); play(el, true, done); }"
    @leave="(el, done) => { playCue('ui.close'); play(el, false, done); }"
    @enter-cancelled="cancel"
    @leave-cancelled="cancel"
  >
    <slot />
  </Transition>
</template>
